import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, Copy, Info, Play,
  RotateCcw, Save, Smartphone, Sparkles,
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button, Badge } from '../../components/ui';
import { STAT_CARD_BASE, STAT_CHIP } from '../../components/ui/StatCard';
import KpiStrip from '../../components/simulation/KpiStrip';
import ReferenceDocumentsPanel from '../../components/simulation/ReferenceDocumentsPanel';
import OverviewEditor from '../../components/simulation/editors/OverviewEditor';
import MetricsEditor from '../../components/simulation/editors/MetricsEditor';
import TeamsEditor from '../../components/simulation/editors/TeamsEditor';
import RoundEditor from '../../components/simulation/editors/RoundEditor';
import DeckEditor, { MIN_CARDS_PER_TEAM } from '../../components/simulation/editors/DeckEditor';
import EventsEditor from '../../components/simulation/editors/EventsEditor';
import simulationService from '../../services/simulationService';
import { showError, showSuccess } from '../../utils/toast';
import { KPI_KEYS, deckFor, findDominantCards, findUniformlyPoorCards } from '../../utils/simulationEngine';

/**
 * Simulation builder — outline rail on the left, an editor for the selected part on
 * the right.
 *
 * Edits go into a local DRAFT and are saved together. Nothing a live run is playing
 * changes: a run freezes its own copy of the simulation when it starts.
 *
 * While the AI is writing, every editor is disabled. The pipeline replaces the whole
 * document when it finishes, so anything typed in the meantime would be lost.
 */

/** The parts of a simulation the builder edits — and the only fields it ever saves. */
const EDITABLE = ['meta', 'kpis', 'functions', 'rounds', 'decisionCards', 'eventCards', 'play'];

const pickEditable = (sim) => EDITABLE.reduce((acc, f) => { acc[f] = sim?.[f]; return acc; }, {});

/** Friendlier names for the AI pipeline's stages in the progress chips. */
const STAGE_LABEL = {
  blueprint: 'Teams and story',
  scenarios: 'Rounds',
  events: 'Outside events',
  debrief: 'Debrief',
};

const num = (v, fallback = 0) =>
  (v === '' || v === null || v === undefined || !Number.isFinite(Number(v)) ? fallback : Number(v));
const cleanLines = (arr) => (arr || []).map((s) => String(s).trim()).filter(Boolean);
const vector = (kpi) => KPI_KEYS.reduce((acc, k) => { acc[k] = num(kpi?.[k]); return acc; }, {});

/**
 * The draft as it will be saved: numbers typed as text become numbers, blank lines and
 * empty voices are dropped, and an empty target means "no target".
 */
const toPayload = (d) => ({
  meta: {
    ...d.meta,
    title: String(d.meta?.title || '').trim(),
    learningObjectives: cleanLines(d.meta?.learningObjectives),
  },
  kpis: (d.kpis || []).map((k) => ({
    ...k,
    label: String(k.label || '').trim(),
    start: num(k.start),
    target: k.target === '' || k.target === null || k.target === undefined ? null : num(k.target, null),
    cap: num(k.cap),
  })),
  functions: (d.functions || []).map((f) => ({ ...f, name: String(f.name || '').trim() })),
  rounds: (d.rounds || []).map((r) => ({
    ...r,
    title: String(r.title || '').trim(),
    decisionTensions: cleanLines(r.decisionTensions),
    conflict: (r.conflict || []).filter((c) => String(c.statement || '').trim()),
    debrief: {
      ...(r.debrief || {}),
      facilitatorQuestions: (r.debrief?.facilitatorQuestions || [])
        .map((q) => ({ ...q, text: String(q.text || '').trim() }))
        .filter((q) => q.text),
    },
  })),
  decisionCards: (d.decisionCards || []).map((c) => ({ ...c, title: String(c.title || '').trim(), kpi: vector(c.kpi) })),
  eventCards: (d.eventCards || []).map((e, i) => ({ ...e, title: String(e.title || '').trim(), order: i + 1, kpi: vector(e.kpi) })),
  play: d.play || {},
});

/**
 * The first thing that would stop a save, with where to find it — or null. Checked
 * here so the trainer lands on the right part instead of reading a server error.
 */
const firstProblem = (p) => {
  if (!p.meta.title) return { node: { type: 'overview' }, message: 'Give the simulation a title' };
  const k = p.kpis.find((x) => !x.label || !(x.cap > 0));
  if (k) return { node: { type: 'kpis' }, message: k.label ? `${k.label} needs a cap above zero` : 'Every metric needs a name' };
  if (p.functions.some((f) => !f.name)) return { node: { type: 'functions' }, message: 'Every team needs a name' };
  for (const r of p.rounds) {
    const node = { type: 'round', index: r.index };
    if (!r.title) return { node, message: `Round ${r.index} needs a title` };
    if ((r.conflict || []).some((c) => !c.speakerKey && !String(c.speakerLabel || '').trim())) {
      return { node, message: `Round ${r.index}: say who the outside voice is` };
    }
  }
  for (const fn of p.functions) {
    const cards = p.decisionCards.filter((c) => c.functionKey === fn.key);
    const node = { type: 'deck', functionKey: fn.key };
    if (cards.length && cards.length < MIN_CARDS_PER_TEAM) return { node, message: `${fn.name} needs at least ${MIN_CARDS_PER_TEAM} options` };
    if (cards.some((c) => !c.title)) return { node, message: `Every ${fn.name} option needs a title` };
  }
  if (p.eventCards.some((e) => !e.title)) return { node: { type: 'events' }, message: 'Every event needs a title' };
  return null;
};

const SimulationBuilderPage = () => {
  const { simulationId } = useParams();
  const navigate = useNavigate();

  /** The saved simulation, as the server last returned it. */
  const [simulation, setSimulation] = useState(null);
  /** What the trainer is editing. Equal to `simulation` until they change something. */
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [node, setNode] = useState({ type: 'overview' });

  /**
   * Arriving from the intake (?step=deck) means the trainer is on step 5 of the
   * client's flow — overview, numbers, teams, rounds, DECISION DECK, then run. Once the
   * AI has written the deck, open it rather than the overview, once.
   */
  const [params] = useSearchParams();
  const fromIntake = params.get('step') === 'deck';
  const openedDeck = useRef(false);

  const dirty = useMemo(
    () => Boolean(draft && simulation) && JSON.stringify(pickEditable(draft)) !== JSON.stringify(pickEditable(simulation)),
    [draft, simulation]
  );

  /** Take a fresh server copy; keep unsaved edits unless told to drop them. */
  const accept = useCallback((sim, { keepDraft = false } = {}) => {
    setSimulation(sim);
    if (!keepDraft) setDraft(sim);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await simulationService.get(simulationId);
        if (alive) accept(res.simulation);
      } catch (err) {
        showError(err.userMessage || 'Could not load this simulation');
        navigate('/trainer/simulations/team');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [simulationId, navigate, accept]);

  /**
   * Generation progress: POLL, don't push.
   *
   * A socket push was wired here originally, but this page has no session and
   * useWebSocket(null) never connects — so the "live" listener could never fire and
   * the page sat on the waiting banner until a manual refresh. The pipeline persists
   * every stage to the document exactly so that a plain re-read is sufficient; this
   * poll is therefore the mechanism, not a fallback.
   *
   * Capped at 10 minutes: a document left in `running` after a server restart is
   * stale by definition (the in-process pipeline is gone), and polling it forever
   * would just spin the network.
   */
  useEffect(() => {
    if (simulation?.generation?.status !== 'running') return undefined;

    let ticks = 0;
    const timer = setInterval(async () => {
      ticks += 1;
      if (ticks > 150) { clearInterval(timer); return; }
      try {
        const res = await simulationService.get(simulationId);
        // Editors are locked while the AI writes, so there are no edits to keep.
        accept(res.simulation);
        const st = res.simulation.generation?.status;
        if (st === 'done') showSuccess('Your simulation is ready');
        if (st === 'failed') showError(res.simulation.generation?.error || 'Generation failed');
      } catch {
        /* transient — keep polling */
      }
    }, 4000);

    return () => clearInterval(timer);
  }, [simulation?.generation?.status, simulationId, accept]);

  useEffect(() => {
    if (!fromIntake || openedDeck.current || !simulation) return;
    if (simulation.generation?.status === 'running' || !simulation.decisionCards?.length) return;
    openedDeck.current = true;
    if (simulation.functions?.[0]) setNode({ type: 'deck', functionKey: simulation.functions[0].key });
  }, [fromIntake, simulation]);

  // Closing the tab or reloading with unsaved edits asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  /** In-app navigation away from unsaved edits asks too. */
  const leave = (fn) => {
    if (dirty && !window.confirm('You have unsaved changes. Leave without saving them?')) return;
    fn();
  };

  const payload = useMemo(() => (draft ? toPayload(draft) : null), [draft]);

  const save = async () => {
    if (!payload) return;
    const problem = firstProblem(payload);
    if (problem) {
      setNode(problem.node);
      showError(problem.message);
      return;
    }
    setSaving(true);
    try {
      const res = await simulationService.update(simulationId, payload);
      accept(res.simulation);
      showSuccess('Saved');
    } catch (err) {
      showError(err.userMessage || err.message || 'Could not save your changes');
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    if (window.confirm('Throw away your unsaved changes?')) setDraft(simulation);
  };

  const duplicate = async () => {
    try {
      const res = await simulationService.clone(simulationId);
      showSuccess('Duplicated — editing the copy');
      navigate(`/trainer/simulations/${res.simulation.id}`);
    } catch (err) {
      showError(err.userMessage || 'Could not duplicate this simulation');
    }
  };

  const writeOptions = async () => {
    try {
      await simulationService.generate(simulationId);
      const r = await simulationService.get(simulationId);
      accept(r.simulation);
    } catch (e) {
      showError(e.userMessage || 'Could not start writing');
    }
  };

  // Advisories read the normalised draft, so they update as the trainer types numbers.
  const advisories = useMemo(() => {
    if (!draft || !payload) return [];
    const sim = { ...draft, ...payload };
    return sim.functions.map((fn) => ({
      fn,
      dominant: findDominantCards(sim, fn.key),
      uniformlyPoor: findUniformlyPoorCards(sim, fn.key),
    }));
  }, [draft, payload]);

  if (loading || !simulation || !draft) {
    return (
      <DashboardLayout title="Simulation builder">
        <div className="w-full px-4 lg:px-6 py-6 max-w-6xl mx-auto">
          <div className={`${STAT_CARD_BASE} animate-pulse p-8`}>
            <div className="h-5 w-1/3 rounded bg-neutral-200 dark:bg-neutral-800" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const status = simulation.generation?.status;
  const writing = status === 'running';
  // Playable only once the AI has written the options — trainer-written rounds alone
  // exist from the moment the draft is created.
  const awaitingOptions = simulation.rounds.length === 0 || simulation.decisionCards.length === 0;

  const set = (field) => (value) => setDraft((d) => ({ ...d, [field]: value }));
  const setRound = (index) => (round) =>
    setDraft((d) => ({ ...d, rounds: d.rounds.map((r) => (r.index === index ? round : r)) }));
  const setDeck = (functionKey) => (cards) =>
    setDraft((d) => ({
      ...d,
      decisionCards: [
        ...d.decisionCards.filter((c) => c.functionKey !== functionKey),
        ...cards.map((c, i) => ({ ...c, order: i + 1 })),
      ],
    }));

  // One chip per stage; a stage's later "done" frame has no detail, so keep the
  // detail from its "running" frame ("Production" rather than "deck:production").
  const stageChips = [...(simulation.generation?.stages || []).reduce((map, x) => {
    const prev = map.get(x.stage);
    map.set(x.stage, { ...prev, ...x, detail: x.detail || prev?.detail });
    return map;
  }, new Map()).values()];

  const railItem = (key, label, meta, active, onClick, indent = false) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs transition-colors ${
        indent ? 'pl-5' : ''
      } ${
        active
          ? 'bg-neutral-900 font-semibold text-white dark:bg-neutral-100 dark:text-neutral-900'
          : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800'
      }`}
    >
      <span className="truncate">{label}</span>
      {meta !== null && meta !== undefined && (
        <span className={`shrink-0 tabular-nums ${active ? 'opacity-70' : 'text-neutral-400'}`}>{meta}</span>
      )}
    </button>
  );

  const selectedRound = node.type === 'round' ? draft.rounds.find((r) => r.index === node.index) : null;
  const selectedFn = node.type === 'deck' ? draft.functions.find((f) => f.key === node.functionKey) : null;

  return (
    <DashboardLayout title="Simulation builder">
      <div className="w-full px-4 lg:px-6 py-6 max-w-6xl mx-auto pb-24 sm:pb-20">

        {/* ---------- header ---------- */}
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <button
              type="button"
              onClick={() => leave(() => navigate('/trainer/simulations/team'))}
              className="mb-1.5 inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
            >
              <ArrowLeft size={13} strokeWidth={2} />
              All simulations
            </button>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                {draft.meta.title || 'Untitled simulation'}
              </h2>
              <Badge variant="default" size="sm">Draft</Badge>
              {dirty && <Badge variant="warning" size="sm">Unsaved changes</Badge>}
            </div>
            <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">
              {[draft.meta.industry, draft.meta.audience].filter(Boolean).join(' · ')}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button variant="ghost" size="sm" onClick={() => leave(duplicate)}>
              <Copy size={14} strokeWidth={2} className="mr-1.5" />
              Duplicate
            </Button>
            <Button variant="secondary" size="sm" onClick={save} loading={saving} disabled={!dirty || saving || writing}>
              <Save size={14} strokeWidth={2} className="mr-1.5" />
              Save
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={awaitingOptions}
              title={awaitingOptions ? 'Needs the decision deck written first' : undefined}
              onClick={() => leave(() => navigate(`/trainer/simulations/${simulation.id}/preview`))}
            >
              <Smartphone size={14} strokeWidth={2} className="mr-1.5" />
              Preview
            </Button>
            <Button
              variant="primary-filled"
              size="sm"
              disabled={awaitingOptions}
              title={awaitingOptions ? 'Needs the decision deck written before it can be played' : undefined}
              onClick={() => leave(() => navigate(`/trainer/simulations/${simulation.id}/rehearse`))}
            >
              <Play size={14} strokeWidth={2} className="mr-1.5" />
              Rehearse
            </Button>
          </div>
        </div>

        {writing && (
          <div className="mb-5 rounded-2xl border border-primary-200 bg-primary-50/60 p-5 dark:border-primary-500/20 dark:bg-primary-500/10">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-primary-300 border-t-primary-600" />
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                  {fromIntake ? 'Step 5 of 5 · Writing the decision deck' : 'Writing your simulation'}
                </h3>
                <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-300">
                  This takes a couple of minutes. You can leave this page — it keeps going, and
                  you&apos;ll find it finished when you come back. Editing unlocks when it&apos;s done.
                </p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {stageChips.map((st) => (
                    <span
                      key={st.stage}
                      className={`rounded-lg px-2 py-1 text-[11px] font-semibold ${
                        st.status === 'running'
                          ? 'bg-primary-100 text-primary-700 dark:bg-primary-500/20 dark:text-primary-300'
                          : st.status === 'warned'
                          ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300'
                          : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300'
                      }`}
                    >
                      {st.stage.startsWith('deck:')
                        ? `${st.detail || st.stage.slice(5)} deck`
                        : STAGE_LABEL[st.stage] || st.detail || st.stage}
                      {st.status === 'done' ? ' ✓' : st.status === 'running' ? ' …' : ' !'}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {status === 'failed' && (
          <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 p-5 dark:border-red-500/20 dark:bg-red-500/10">
            <h3 className="text-sm font-semibold text-red-900 dark:text-red-200">Generation failed</h3>
            <p className="mt-1 text-sm text-red-800 dark:text-red-300">{simulation.generation.error}</p>
            {/* Resume is server-side: retrying reuses every stage that already
                succeeded, so it is fast and cheap — worth saying, because "failed"
                otherwise reads as "start the two minutes again". Not shown for
                daily-quota failures, where retrying now cannot help. */}
            {simulation.generation.errorCode !== 'quota_daily' &&
              Object.keys(simulation.generation.checkpoint || {}).length > 0 && (
              <p className="mt-1.5 text-xs text-red-700/80 dark:text-red-300/80">
                The parts already written are kept — trying again picks up where it stopped.
              </p>
            )}
            <Button variant="secondary" size="sm" className="mt-3" onClick={writeOptions}>
              Try again
            </Button>
          </div>
        )}

        {awaitingOptions && !writing && status !== 'failed' && (
          <div className="mb-5 rounded-2xl border border-primary-200 bg-primary-50/60 p-5 dark:border-primary-500/20 dark:bg-primary-500/10">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-primary-600 dark:bg-neutral-900 dark:text-primary-400">
                <Sparkles size={17} strokeWidth={1.75} />
              </span>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                  The decision deck isn&apos;t written yet
                </h3>
                <p className="mt-1 max-w-2xl text-sm leading-relaxed text-neutral-600 dark:text-neutral-300">
                  We have your overview, {simulation.kpis.length} metrics,{' '}
                  {simulation.functions.length} teams{simulation.rounds.length ? ` and ${simulation.rounds.length} rounds` : ''}.
                  The AI writes the decision deck — each team&apos;s options — plus their point of
                  view in each round, the outside events and the debrief.
                </p>
                {simulation.intake?.brief && (
                  <Button variant="primary-filled" size="sm" className="mt-3" onClick={() => leave(writeOptions)}>
                    <Sparkles size={14} strokeWidth={2} className="mr-1.5" />
                    Write the decision deck
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 5 of the client's flow: review the AI's decision deck, then run. */}
        {fromIntake && !writing && status !== 'failed' && !awaitingOptions && (
          <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-5 dark:border-emerald-500/20 dark:bg-emerald-500/10">
            <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              Step 5 of 5 · Review the decision deck
            </h3>
            <p className="mt-1 max-w-3xl text-sm leading-relaxed text-neutral-600 dark:text-neutral-300">
              The AI has written each team&apos;s options. Go through every team under{' '}
              <span className="font-medium">Decision deck</span> on the left — edit, add or remove
              options and what each does to the numbers — then save. When it looks right,
              rehearse it, or run it live from a session with <span className="font-medium">Run a simulation</span>.
            </p>
          </div>
        )}

        <div className="grid gap-5 lg:grid-cols-[210px_1fr]">
          {/* ---------- outline rail ---------- */}
          <aside className={`${STAT_CARD_BASE} h-fit p-3 lg:sticky lg:top-4`}>
            <div className="space-y-0.5">
              {railItem('overview', 'Overview', null, node.type === 'overview', () => setNode({ type: 'overview' }))}
              {railItem('kpis', 'The numbers', draft.kpis.length, node.type === 'kpis', () => setNode({ type: 'kpis' }))}
              {railItem('functions', 'Teams', draft.functions.length, node.type === 'functions', () => setNode({ type: 'functions' }))}

              <p className="px-2.5 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
                Rounds
              </p>
              {draft.rounds.map((r) =>
                railItem(
                  `round-${r.index}`,
                  `${r.index}. ${(r.title || 'Untitled').split('—')[0].trim()}`,
                  null,
                  node.type === 'round' && node.index === r.index,
                  () => setNode({ type: 'round', index: r.index }),
                  true
                )
              )}

              {draft.decisionCards.length > 0 && (
                <>
                  <p className="px-2.5 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
                    Decision deck
                  </p>
                  {draft.functions.map((f) =>
                    railItem(
                      `deck-${f.key}`,
                      f.name,
                      deckFor(draft, f.key).length,
                      node.type === 'deck' && node.functionKey === f.key,
                      () => setNode({ type: 'deck', functionKey: f.key }),
                      true
                    )
                  )}
                </>
              )}

              <div className="pt-3">
                {railItem('events', draft.meta.eventDeckLabel || 'Outside events', draft.eventCards.length, node.type === 'events', () => setNode({ type: 'events' }))}
                {railItem('sources', 'Reference docs', (simulation.sources || []).length || null, node.type === 'sources', () => setNode({ type: 'sources' }))}
                {railItem('checks', 'Checks', advisories.reduce((n, a) => n + a.dominant.length, 0), node.type === 'checks', () => setNode({ type: 'checks' }))}
              </div>
            </div>
          </aside>

          {/* ---------- editor ---------- */}
          {/* A disabled fieldset disables every control inside it: the whole editor is
              read-only while the AI writes, with no per-input plumbing. */}
          <fieldset disabled={writing} className="min-w-0 space-y-4">
            {node.type === 'overview' && (
              <>
                <OverviewEditor
                  meta={draft.meta}
                  play={draft.play || {}}
                  rounds={draft.rounds}
                  onMeta={set('meta')}
                  onPlay={set('play')}
                  onRounds={set('rounds')}
                />

                <div className={`${STAT_CARD_BASE} p-5`}>
                  <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                    <Info size={14} strokeWidth={1.75} className="text-neutral-400" />
                    Where this came from
                  </h3>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    {(simulation.provenance?.source || []).join(' · ') || 'Written in the builder.'}
                    {simulation.provenance?.canonicalSheet && (
                      <>
                        {' '}Scoring uses the{' '}
                        <span className="font-medium">{simulation.provenance.canonicalSheet}</span> tab.
                      </>
                    )}
                  </p>
                  {(simulation.provenance?.conflicts || []).length > 0 && (
                    <div className="mt-3 space-y-2">
                      {simulation.provenance.conflicts.map((c) => (
                        <div key={c.detail} className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 dark:bg-amber-500/10">
                          <AlertTriangle size={13} strokeWidth={1.75} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                          <p className="text-[11px] leading-relaxed text-amber-800 dark:text-amber-200">
                            <span className="font-semibold">{c.field}</span> — {c.detail}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {node.type === 'sources' && (
              <ReferenceDocumentsPanel simulationId={simulationId} />
            )}

            {node.type === 'kpis' && (
              <>
                <KpiStrip kpis={payload.kpis} values={{}} showBar={false} showTargetStatus={false} />
                <MetricsEditor kpis={draft.kpis} onChange={set('kpis')} />
              </>
            )}

            {node.type === 'functions' && (
              <TeamsEditor functions={draft.functions} kpis={draft.kpis} onChange={set('functions')} />
            )}

            {node.type === 'round' && selectedRound && (
              <RoundEditor round={selectedRound} functions={draft.functions} onChange={setRound(node.index)} />
            )}

            {node.type === 'deck' && selectedFn && (
              <DeckEditor
                fn={selectedFn}
                cards={deckFor(draft, selectedFn.key)}
                kpis={draft.kpis}
                onChange={setDeck(selectedFn.key)}
              />
            )}

            {node.type === 'events' && (
              <EventsEditor
                label={draft.meta.eventDeckLabel || 'Outside events'}
                events={draft.eventCards}
                kpis={draft.kpis}
                onChange={set('eventCards')}
              />
            )}

            {node.type === 'checks' && (
              <div className="space-y-3">
                <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-500/20 dark:bg-blue-500/10">
                  <div className="flex items-start gap-2.5">
                    <Info size={15} strokeWidth={1.75} className="mt-0.5 shrink-0 text-blue-600 dark:text-blue-400" />
                    <p className="text-xs leading-relaxed text-blue-900 dark:text-blue-200">
                      These are <span className="font-semibold">advisories, not errors</span>. Nothing
                      here is auto-corrected. A deck with obviously-weak options is often exactly
                      right — the blame and avoidance cards are supposed to cost you.
                    </p>
                  </div>
                </div>

                {advisories.map(({ fn, dominant, uniformlyPoor }) => (
                  <div key={fn.key} className={`${STAT_CARD_BASE} p-5`}>
                    <span className={`rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STAT_CHIP[fn.variant] || STAT_CHIP.slate}`}>
                      {fn.name}
                    </span>

                    {dominant.length === 0 ? (
                      <p className="mt-3 text-xs text-emerald-600 dark:text-emerald-400">
                        No dominant option — every choice costs something.
                      </p>
                    ) : (
                      <div className="mt-3 space-y-2">
                        {dominant.map((d) => (
                          <div key={d.card.id} className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 dark:bg-amber-500/10">
                            <AlertTriangle size={13} strokeWidth={1.75} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                            <p className="text-[11px] leading-relaxed text-amber-800 dark:text-amber-200">
                              <span className="font-semibold">{d.card.title}</span> beats{' '}
                              {d.dominatesCount} of {deckFor(draft, fn.key).length - 1} alternatives
                              on every metric{d.dominatesAll && ' — it is strictly the best card in the deck'}.
                              Teams that spot it will stop deliberating.
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {uniformlyPoor.length > 0 && (
                      <p className="mt-2 text-[11px] text-neutral-500 dark:text-neutral-400">
                        {uniformlyPoor.length} card{uniformlyPoor.length > 1 ? 's' : ''} cost on every
                        metric ({uniformlyPoor.map((c) => c.title).join(', ')}). Expected — these are
                        the behaviours the debrief is about.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </fieldset>
        </div>

        <p className="mt-6 text-xs leading-relaxed text-neutral-400 dark:text-neutral-500">
          Changes apply from the next run. A run already in progress keeps the version it started with.
        </p>
      </div>

      {/* ---------- unsaved changes ---------- */}
      {dirty && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-neutral-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-neutral-700 dark:bg-neutral-900/95">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-neutral-600 dark:text-neutral-300">You have unsaved changes.</p>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={discard} disabled={saving}>
                <RotateCcw size={14} strokeWidth={2} className="mr-1.5" />
                Discard
              </Button>
              <Button variant="primary-filled" size="sm" onClick={save} loading={saving} disabled={saving || writing}>
                <Save size={14} strokeWidth={2} className="mr-1.5" />
                Save changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default SimulationBuilderPage;
